<template>
  <div class="fts-invitation comfort-form-design">
    <!-- user profile -->
    <div class="fts-form-group" v-if="isDevelopment">
      <fts-form-label icon="fts-icon-user" placement="top">
        User Profile (optional)&nbsp;<fts-release-badge dev class="fts-align-top" />
      </fts-form-label>
      <fts-select
        :options="workspaceProfiles"
        v-model="formData.userProfile"
        comfort-design
        @option:selected="userProfileSelected"
      >
        <fts-label-group
          slot="selected-option-container"
          slot-scope="{ option }"
          :item="option"
          :list="workspaceProfiles"
          emoji
        />
        <fts-label-group
          slot="option"
          slot-scope="{ option }"
          :item="option"
          :list="workspaceProfiles"
          emoji
        />
      </fts-select>
    </div>
    <!-- emails -->
    <div>
      <fts-form-label placement="top">Send to</fts-form-label>
      <div class="fts-form-control textarea invite-box fts-scrollbar fts-scrollbar-overlay" 
        :class="{focus: inputFocused}" @click="focus">
        <div class="invite-placeholder" v-if="invitePlaceholderVisible">example@mail.com</div>
        <div class="invite-box-field ">
          <div 
            class="invite-email" 
            :class="{error: !x.valid}"
            v-for="x in inviteEmails" 
            :key="x.id">
            <div>{{x.email}}</div>
            <font-awesome-icon :icon="['fas', 'times']" class="remove-email" @click="removeEmail(x)" />
          </div>
          <input 
            type="text"
            v-model="inviteInput" 
            @focus="inputFocused=true;withoutEmailError=false" 
            @blur="inputFocused=false;insertEmail()" 
            @keyup="inviteInputKeyup"
            @keydown.delete="inputDelete"
            @keydown.space.prevent
            @keydown.188.prevent
            @paste.prevent="inviteInputOnPaste"
            autocomplete="inviteInput"
            ref="inviteInput">
        </div>
      </div>

      <div class="invite-email-action">
        <fts-btn
          color="primary"
          outline text-transform-init   
          :loading="loading"
          @click.native.prevent="submit"
        >
          Invite
        </fts-btn>
      </div>

      <div class="fts-form-error" v-if="emailError">
        Invalid email format.
      </div>

      <div class="fts-form-error" v-else-if="withoutEmailError">
        Must have at least an email.
      </div>
    </div>
    
    <div class="invite-label invite-label--my">
      Or, send a Invite Link
    </div>
    <!-- invitation link -->
    <div class="fts-form-group">
      <template v-if="invitationLink">
        <fts-clipboard :copy="invitationLink" :loading="invitationLinkLoading">
          {{invitationLink}}
        </fts-clipboard>
        <div class="invite-label-hint">
          To ensure a seamless experience for mobile users, we highly recommend downloading the Inistate App and then clicking on the provided invite link.
        </div>
      </template>
      <div v-else>
        <fts-btn
          outline
          color="primary"
          text-transform-init
          :loading="invitationLinkLoading"
          @click="getInvitationLink"
        >
          Get Invitation Link
        </fts-btn>
      </div>
    </div>
    <!-- feedback indicate -->
    <fts-feedback-indicate modal ref="ftsFeedbackIndicate" />
  </div>
</template>

<script>
import commons from '../commons/commons';
import { emailValidator } from "../commons/validator";
import { clientStore } from '../store/clientStore';
import { authorizationStore } from '../store/authorizationStore';

import FTSBtn from "../elements/FTSBtn";
import FTSClipboard from "../elements/FTSClipboard";
import FTSFormLabel from "../elements/FTSFormLabel";
import FTSFeedbackIndicate from "../elements/FTSFeedbackIndicate";
import FTSLabelGroup from "../elements/FTSLabelGroup";
import FTSSelect from "../elements/FTSSelect";

let inviteInputCache = null;

export default {
  components: {
    "fts-btn": FTSBtn,
    "fts-clipboard": FTSClipboard,
    "fts-form-label": FTSFormLabel,
    "fts-feedback-indicate": FTSFeedbackIndicate,
    "fts-label-group": FTSLabelGroup,
    "fts-select": FTSSelect
  },
  data: () => ({
    loading: false,
    error: false,
    formData: {},
    inputFocused: false,
    withoutEmailError: false,
    inviteInput: null,
    inviteEmails: [],
    invitationLink: null,
    invitationLinkLoading: false
  }),
  methods: {
    async getInvitationLink() {
      try {
        this.invitationLinkLoading = true;
        this.invitationLink = await clientStore.dispatch('getInvitationLink', {});
      } catch(err) {
        console.error(err);
        this.$toasted.global.error({ response: err.response });
      } finally {
        this.invitationLinkLoading = false;
      }
    },
    focus() {
      this.$refs.inviteInput.focus();
    },
    inviteInputKeyup(evt) {
      if ([13, 32, 188].some(x => x === evt.keyCode)) 
        this.insertEmail()
    },
    inviteInputOnPaste(evt) {
      try {
        let emails = evt.clipboardData.getData('text/plain');
        emails = emails.replaceAll(/\s/g,'').split(",");
        emails.forEach(x => this.insertEmail(x));
      } catch(err) {}
    },
    insertEmail(data) {
      const email = this.inviteInput || data 
      if (email && email.length && !this.inviteEmails.some(x => x.email === email)) {
        this.inviteEmails.push({
          id: commons.nanoID(),
          email: email,
          valid: emailValidator(email),
        })
        this.inviteInput = null
      }
    },  
    removeEmail(data) {     
      const index = data ? this.inviteEmails.indexOf(data) : -1
      this.inviteEmails.splice(index, 1);
    },
    inputDelete() {
      inviteInputCache = this.inviteInput
      if (!this.inviteInput && !inviteInputCache && this.inviteEmails && this.inviteEmails.length) 
        this.removeEmail()
    },
    async submit() {
      this.withoutEmailError = !(this.inviteEmails && this.inviteEmails.length);
      if (this.emailError || this.withoutEmailError) {
        return;
      } 
      try {
        this.loading = true;
        let result = await clientStore.dispatch('sendInvitation', {
          emails: [...this.inviteEmails.map(x => x.email)]
        });
        this.$refs.ftsFeedbackIndicate.show({
          type: 'success',
          desc: result.data && result.data.message? result.data.message : "Invitation successfully send.",
          handler: () => {
            this.$emit("close");

            // SS06325: the people list is no longer only on the "users" route - since Settings went
            // live, /users folds into Settings > People, which runs under "settings". Only a mounted
            // people list listens for this, so it is safe to say on every route.
            window.eventBus.$emit("users:refresh");
          },
        })
      } catch(err) {
        this.$refs.ftsFeedbackIndicate.show({
          type: 'error',
          desc: 'Invitation failed to send, please try again later.',
          response: err.response,
        });
        console.error(err);
      } finally {    
        this.loading = false;
      }
    },
    userProfileSelected() {
      Boolean(this.invitationLink) && this.getInvitationLink();
    }
  },
  computed: {
    isDevelopment() {
      return authorizationStore.getters.isDevelopment;
    },
    workspaceProfiles() {
      return clientStore.getters.workspaceModules && clientStore.getters.workspaceModules.filter((x) => x.isProfile);
    },
    invitePlaceholderVisible() {
      return !this.inviteInput && !this.inviteEmails.length;
    },
    emailError() {
      return this.inviteEmails.filter(x => !x.valid ).length && authorizationStore.state.userProfile.userName != 'admin';
    }
  }
}
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.invite-box {
  min-height: 88px;
  position: relative;
  &.focus {
    border-color: var(--fts-primary);
    outline: none;
    box-shadow: $input-box-shadow;
    -webkit-box-shadow: $input-box-shadow;
  }
  .invite-box-field {
    @include flex-gap($gap: 0.375rem);
    > input {
      flex: 1;
      padding: .375rem 0;
      border: 0;
      font-size: 1rem;
      background: inherit;
      color: inherit;
    }
  }
  .invite-placeholder {
    position: absolute;
    padding: .375rem 0;
    color: var(--fts-form-control-placeholder);
    line-height: 1.15;
  }
  .invite-email {
    @include theme() {
      background: rgba($color: theme-get('fts-primary'), $alpha: 0.15);
    }
    display: flex;
    align-items: center;
    padding: .375rem .575rem;
    border-radius: .25rem;
    line-height: 1.15;
    &.error {
      @include theme() {
        background: rgba($color: theme-get('fts-danger'), $alpha: 0.1);
      }
    }
    .remove-email {
      margin-left: .575rem;
      font-size: .75rem;
      color: var(--fts-default);
      &:hover {
        cursor: pointer;
      }
    }
  }
}
.invite-error {
  display: flex;
  align-items: center;
  margin-top: 1rem;
  margin-left: .135rem;
  font-size: .875rem;
  color: var(--fts-danger);
  .error-icon {
    margin-right: .575rem;
  }
}
.invite-email-action {
  margin-top: 0.5rem;
}
.invite-label {
  font-size: $font-size;
  line-height: $line-height;
  color: var(--fts-default);
  &.invite-label--mb {
    margin-bottom: 0.375rem;
  }
  &.invite-label--my {
    margin-top: 1rem;
    margin-bottom: 0.375rem;
  }
}
.invite-label-hint {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: var(--fts-form-helper);
}
</style>